/**
 * 智能版本管理器
 * 简化版本检查和更新机制
 */

export class VersionManager {
  private static VERSION_KEY = 'app_version';
  private static CHECK_INTERVAL = 3600000; // 1小时
  private static LAST_CHECK_KEY = 'last_version_check';
  
  /**
   * 检查版本更新
   * 限制检查频率，避免过度请求
   */
  static async checkForUpdates(): Promise<boolean> {
    // 确保在客户端环境
    if (typeof window === 'undefined') return false;
    
    // 检查是否需要进行版本检查
    const lastCheck = localStorage.getItem(this.LAST_CHECK_KEY);
    const now = Date.now();
    
    if (lastCheck && now - Number(lastCheck) < this.CHECK_INTERVAL) {
      return false;
    }
    
    try {
      const response = await fetch('/api/build-info', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }
      
      const { buildId } = await response.json();
      const currentVersion = localStorage.getItem(this.VERSION_KEY);
      
      // 更新检查时间
      localStorage.setItem(this.LAST_CHECK_KEY, String(now));
      
      // 如果版本不同，说明有更新
      if (currentVersion && buildId !== currentVersion) {
        localStorage.setItem(this.VERSION_KEY, buildId);
        return true;
      }
      
      // 如果是首次访问，记录版本
      if (!currentVersion) {
        localStorage.setItem(this.VERSION_KEY, buildId);
      }
      
      return false;
    } catch (error) {
      console.error('Version check failed:', error);
      return false;
    }
  }
  
  /**
   * 应用更新
   * 智能清理缓存并刷新页面
   */
  static async applyUpdate() {
    // 确保在客户端环境
    if (typeof window === 'undefined') return;
    
    // 1. 保存用户数据
    const userDataKeys = [
      'auth_token',
      'user_preferences',
      'locale',
      'theme',
      'sfund_locale_user',
      'sfund_locale_admin'
    ];
    
    const userData: Record<string, string | null> = {};
    userDataKeys.forEach(key => {
      userData[key] = localStorage.getItem(key);
    });
    
    // 2. 清理特定缓存
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => {
            // 只清理应用相关缓存，保留浏览器缓存
            if (name.includes('sfund') || name.includes('next-')) {
              return caches.delete(name);
            }
            return Promise.resolve();
          })
        );
      } catch (error) {
        console.error('Failed to clear caches:', error);
      }
    }
    
    // 3. 恢复用户数据
    Object.entries(userData).forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });
    
    // 4. 通知 Service Worker 跳过等待
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // 5. 重新加载页面
    window.location.reload();
  }
  
  /**
   * 获取当前版本
   */
  static getCurrentVersion(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.VERSION_KEY);
  }
  
  /**
   * 重置版本检查时间
   * 用于强制检查更新
   */
  static resetCheckTimer() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.LAST_CHECK_KEY);
  }
}